import React, { useEffect, useMemo, useState } from "react";
import {
    Badge,
    Box,
    Button,
    ChoiceList,
    IndexFilters,
    IndexTable,
    LegacyCard,
    SkeletonBodyText,
    Text,
    useSetIndexFiltersMode,
} from "@shopify/polaris";
import type { FilterInterface, IndexFiltersProps, TabProps } from "@shopify/polaris";
import { ChevronDownIcon, ChevronRightIcon, RefreshIcon } from "@shopify/polaris-icons";
import { useNavigate } from "react-router-dom";
import { ABTestRow, ABTestStatus } from "../types";
import { BundleTypeDisplayNames } from "../../Analytics/constants/configs";
import CustomTooltipAdmin from "../../../components/CustomTooltipAdmin/CustomTooltipAdmin";
import { PAGE_SIZE } from "../../constants";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// e.g. "Jul 23, 2026" — used for the Created date on running/draft tests.
const formatCreatedDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// e.g. "Jul 16, '26" — used inside the completed-test period range.
const formatShortYearDate = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const formatted = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
    return formatted.replace(/,\s*(\d{2})$/, ", '$1");
};

const pluralize = (count: number, unit: string) => `${count} ${unit}${count === 1 ? "" : "s"}`;

// Picks the coarsest unit (day/week/month/year/decade) that keeps the count
// small and readable, rather than always showing raw day counts.
const formatDurationLabel = (days: number) => {
    if (days < 7) return pluralize(days, "day");
    if (days < 30) return pluralize(Math.round(days / 7), "week");
    if (days < 365) return pluralize(Math.round(days / 30), "month");
    if (days < 3650) return pluralize(Math.round(days / 365), "year");
    return pluralize(Math.round(days / 3650), "decade");
};

// e.g. "6 days (Jul 16, '26-Jul 23, '26)" — used for completed tests.
const formatCompletedPeriod = (start?: string | null, end?: string | null) => {
    if (!start) return "-";
    if (!end) return formatShortYearDate(start);
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return "-";
    const days = Math.round((endDate.getTime() - startDate.getTime()) / MS_PER_DAY) + 1;
    return `${formatDurationLabel(days)} (${formatShortYearDate(start)} - ${formatShortYearDate(end)})`;
};

const money = (value: number) => `$${(value || 0).toFixed(2)}`;

export const TABS = [
    { id: "running", content: "Running" },
    { id: "draft", content: "Draft" },
    { id: "completed", content: "Completed" },
];

// Bundle types A/B testing can apply to. Kept in sync manually with kefi-be's
// BUNDLE_TYPE constant — today only "volume" produces real ab_config data,
// but the filter stays generic so it doesn't need touching when more bundle
// types gain A/B testing support.
export const BUNDLE_TYPE_VALUES = [
    // "standard",
    "volume",
    // "bxgy",
    // "mixmatch",
    // "fbt",
    // "product_add_on",
    // "product_add_on_popup",
    // "mixmatch_sectional",
    "bundle_builder",
    // "bb_placeholder",
    // "buy_n",
];

const BUNDLE_TYPE_CHOICES = BUNDLE_TYPE_VALUES.map((value) => ({
    label: BundleTypeDisplayNames[value] ?? value,
    value,
}));

type DisplayRow =
    | { kind: "bundle"; row: ABTestRow }
    | { kind: "variant"; row: ABTestRow; variant: ABTestRow["variants"][number] };

interface ABTestTableProps {
    rows: ABTestRow[];
    loading: boolean;
    status: ABTestStatus;
    selectedTabIndex: number;
    onTabChange: (index: number) => void;
    selectedBundleTypes: string[];
    onBundleTypesChange: (values: string[]) => void;
    onSetWinner: (row: ABTestRow, variantId: string) => void;
    onStartOver: (row: ABTestRow) => void;
    hideFilters?: boolean;
}

const headingTooltip = (label: string, content: string) => (
    <CustomTooltipAdmin content={content} hasUnderline={true}>
        <span>{label}</span>
    </CustomTooltipAdmin>
);

const getHeadings = (status: ABTestStatus) => [
    { title: headingTooltip("Name", "") },
    {
        title: headingTooltip(
            status === "completed" ? "Period/Date" : "Created",
            status === "completed" ? "The date range this A/B test ran for." : "The date this A/B test was created.",
        ),
    },
    { title: headingTooltip("Visitors", "Unique visitors who saw this variant's bundle widget.") },
    { title: headingTooltip("Views", "Total number of times this variant's bundle widget was viewed.") },
    { title: headingTooltip("Clicks", "Total number of clicks on this variant's bundle widget.") },
    { title: headingTooltip("Orders", "Total number of orders attributed to this bundle.") },
    { title: headingTooltip("CR", "Conversion rate: orders divided by visitors.") },
    { title: headingTooltip("AOV", "Average order value: revenue divided by orders.") },
    { title: headingTooltip("Revenue", "Total revenue attributed to orders that included this bundle.") },
    { title: headingTooltip("Actions", "") },
];

const ABTestTable: React.FC<ABTestTableProps> = ({
    rows,
    loading,
    status,
    selectedTabIndex,
    onTabChange,
    selectedBundleTypes,
    onBundleTypesChange,
    onSetWinner,
    onStartOver,
    hideFilters = false,
}) => {
    const goTo = useNavigate();
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const { mode, setMode } = useSetIndexFiltersMode();
    const [currentPage, setCurrentPage] = useState(0);

    useEffect(() => {
        setCurrentPage(0);
    }, [status, selectedBundleTypes]);

    useEffect(() => {
        const lastPage = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1);
        setCurrentPage((prevPage) => Math.min(prevPage, lastPage));
    }, [rows.length]);

    const toggle = (expId: string) => {
        setExpanded((prev) => ({ ...prev, [expId]: !(prev[expId] ?? true) }));
    };

    const startIndex = currentPage * PAGE_SIZE;
    const paginatedRows = rows.slice(startIndex, startIndex + PAGE_SIZE);

    const displayRows: DisplayRow[] = useMemo(() => {
        return paginatedRows.flatMap((row) => {
            const isOpen = expanded[row.exp_id] ?? true;
            const bundleRow: DisplayRow = { kind: "bundle", row };
            const variantRows: DisplayRow[] = isOpen
                ? row.variants.map((variant) => ({ kind: "variant", row, variant }))
                : [];
            return [bundleRow, ...variantRows];
        });
    }, [paginatedRows, expanded]);

    const HEADINGS = useMemo(() => getHeadings(status), [status]);

    const resourceName = { singular: "A/B test", plural: "A/B tests" };

    const tabs: TabProps[] = TABS.map((tab, index) => ({
        content: tab.content,
        index,
        onAction: () => onTabChange(index),
        id: `${tab.id}-${index}`,
        disabled: loading,
    }));

    const filters: FilterInterface[] = [
        {
            key: "bundle_type",
            label: "Category",
            pinned: true,
            hideClearButton: true,
            filter: (
                <ChoiceList
                    title="Category"
                    titleHidden
                    allowMultiple
                    choices={BUNDLE_TYPE_CHOICES}
                    selected={selectedBundleTypes}
                    onChange={onBundleTypesChange}
                />
            ),
        },
    ];

    const appliedFilters: IndexFiltersProps["appliedFilters"] = [];
    if (selectedBundleTypes.length && selectedBundleTypes.length < BUNDLE_TYPE_VALUES.length) {
        appliedFilters.push({
            key: "bundle_type",
            label: `Category: ${selectedBundleTypes.map((value) => BundleTypeDisplayNames[value] ?? value).join(", ")}`,
            onRemove: () => onBundleTypesChange(BUNDLE_TYPE_VALUES),
        });
    }

    const rowShimmer = Array(5)
        .fill(0)
        .map((_, index) => (
            <IndexTable.Row id={`shimmer-${index}`} key={`shimmer-${index}`} position={index}>
                {HEADINGS.map((_, cellIndex) => (
                    <IndexTable.Cell key={cellIndex}>
                        <Box paddingBlock="100">
                            <SkeletonBodyText lines={1} />
                        </Box>
                    </IndexTable.Cell>
                ))}
            </IndexTable.Row>
        ));

    return (
        <LegacyCard>
            {!hideFilters && (
                <IndexFilters
                    tabs={tabs}
                    selected={selectedTabIndex}
                    onSelect={onTabChange}
                    filters={filters}
                    appliedFilters={appliedFilters}
                    onClearAll={() => onBundleTypesChange(BUNDLE_TYPE_VALUES)}
                    queryValue=""
                    onQueryChange={() => {}}
                    onQueryClear={() => {}}
                    hideQueryField
                    filteringAccessibilityLabel="Filter results"
                    filteringAccessibilityTooltip="Filter"
                    cancelAction={{
                        onAction: () => onBundleTypesChange(BUNDLE_TYPE_VALUES),
                        disabled: false,
                        loading: false,
                    }}
                    canCreateNewView={false}
                    mode={mode}
                    setMode={setMode}
                />
            )}
            <IndexTable
                {...(!loading &&
                    rows.length > PAGE_SIZE && {
                        pagination: {
                            hasNext: currentPage < Math.ceil(rows.length / PAGE_SIZE) - 1,
                            hasPrevious: currentPage > 0,
                            onNext() {
                                setCurrentPage((prevPage) => prevPage + 1);
                            },
                            onPrevious() {
                                setCurrentPage((prevPage) => prevPage - 1);
                            },
                            label: `Showing ${PAGE_SIZE * currentPage + 1}-${Math.min(
                                PAGE_SIZE * (currentPage + 1),
                                rows.length,
                            )} of ${rows.length} ${rows.length === 1 ? resourceName.singular : resourceName.plural}`,
                        },
                    })}
                resourceName={resourceName}
                itemCount={loading ? 5 : displayRows.length}
                selectable={false}
                headings={HEADINGS as any}
                emptyState={
                    <div className="Polaris-LegacyStack Polaris-LegacyStack--vertical Polaris-LegacyStack--alignmentCenter">
                        <div className="Polaris-LegacyStack__Item">
                            <img
                                alt="Empty search results"
                                src="data:image/svg+xml,%3csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' d='M41.87 24a17.87 17.87 0 11-35.74 0 17.87 17.87 0 0135.74 0zm-3.15 18.96a24 24 0 114.24-4.24L59.04 54.8a3 3 0 11-4.24 4.24L38.72 42.96z' fill='%238C9196'/%3e%3c/svg%3e"
                                draggable="false"
                            />
                        </div>
                        <div className="Polaris-LegacyStack__Item">
                            <p className="Polaris-Text--root Polaris-Text--headingLg">
                                {status === "running"
                                    ? "No running A/B tests"
                                    : status === "draft"
                                      ? "No draft A/B tests"
                                      : "No completed A/B tests yet"}
                            </p>
                        </div>
                        <div className="Polaris-LegacyStack__Item">
                            <span className="Polaris-Text--root Polaris-Text--subdued">
                                <p>
                                    <Button onClick={() => goTo("/")} variant={"primary"}>
                                        {"Create Offer"}
                                    </Button>
                                </p>
                            </span>
                        </div>
                    </div>
                }
            >
                {loading
                    ? rowShimmer
                    : displayRows.map((entry, index) => {
                          if (entry.kind === "bundle") {
                              const { row } = entry;
                              const isOpen = expanded[row.exp_id] ?? true;
                              return (
                                  <IndexTable.Row id={row.exp_id} key={row.exp_id} position={index}>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                  <Button
                                                      variant="plain"
                                                      icon={isOpen ? ChevronDownIcon : ChevronRightIcon}
                                                      onClick={() => toggle(row.exp_id)}
                                                      accessibilityLabel="Toggle variants"
                                                  />
                                                  <Text as="span" fontWeight="medium">
                                                      {row.title || row.form_id}
                                                  </Text>
                                                  {/* <Badge tone="success">A/B</Badge> */}
                                                  <Badge>
                                                      {BundleTypeDisplayNames[row.bundle_type] ?? row.bundle_type}
                                                  </Badge>
                                              </div>
                                          </Box>
                                      </IndexTable.Cell>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              <Text as="span">
                                                  {status === "completed"
                                                      ? formatCompletedPeriod(row.start_date, row.end_date)
                                                      : formatCreatedDate(row.start_date)}
                                              </Text>
                                          </Box>
                                      </IndexTable.Cell>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              <Text as="span">{row.stats.visitors}</Text>
                                          </Box>
                                      </IndexTable.Cell>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              <Text as="span">{row.stats.views}</Text>
                                          </Box>
                                      </IndexTable.Cell>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              <Text as="span">{row.stats.clicks}</Text>
                                          </Box>
                                      </IndexTable.Cell>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              <Text as="span">{row.stats.orders}</Text>
                                          </Box>
                                      </IndexTable.Cell>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              <Text as="span">{row.stats.cr}%</Text>
                                          </Box>
                                      </IndexTable.Cell>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              <Text as="span">{money(row.stats.aov)}</Text>
                                          </Box>
                                      </IndexTable.Cell>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              <Text as="span">{money(row.stats.added_revenue)}</Text>
                                          </Box>
                                      </IndexTable.Cell>
                                      <IndexTable.Cell>
                                          <Box paddingBlock="100">
                                              {status === "completed" ? (
                                                  <Button
                                                      variant="plain"
                                                      icon={RefreshIcon}
                                                      onClick={() => onStartOver(row)}
                                                  >
                                                      Start over
                                                  </Button>
                                              ) : null}
                                          </Box>
                                      </IndexTable.Cell>
                                  </IndexTable.Row>
                              );
                          }

                          const { row, variant } = entry;
                          const rowId = `${row.exp_id}-${variant.id}`;
                          return (
                              <IndexTable.Row id={rowId} key={rowId} position={index}>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          <div
                                              style={{
                                                  display: "inline-flex",
                                                  gap: 6,
                                                  paddingLeft: 32,
                                              }}
                                          >
                                              <Text as="span">{variant.label} variant</Text>
                                              {/* <Icon source={ViewIcon} tone="subdued" /> */}
                                              {variant.is_winner ? <Badge tone="success">Winner</Badge> : null}
                                          </div>
                                      </Box>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          <Text as="span">-</Text>
                                      </Box>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          <Text as="span">{variant.stats.visitors}</Text>
                                      </Box>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          <Text as="span">{variant.stats.views}</Text>
                                      </Box>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          <Text as="span">{variant.stats.clicks}</Text>
                                      </Box>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          <Text as="span">{variant.stats.orders}</Text>
                                      </Box>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          <Text as="span">{variant.stats.cr}%</Text>
                                      </Box>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          <Text as="span">{money(variant.stats.aov)}</Text>
                                      </Box>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          <Text as="span">{money(variant.stats.added_revenue)}</Text>
                                      </Box>
                                  </IndexTable.Cell>
                                  <IndexTable.Cell>
                                      <Box paddingBlock="100">
                                          {(status === "running" || status === "draft") && !variant.is_winner ? (
                                              <Button variant="plain" onClick={() => onSetWinner(row, variant.id)}>
                                                  Set winner
                                              </Button>
                                          ) : null}
                                      </Box>
                                  </IndexTable.Cell>
                              </IndexTable.Row>
                          );
                      })}
            </IndexTable>
        </LegacyCard>
    );
};

export default ABTestTable;
